import { useMemo } from "react";
import { type DecoratedFormProps, SubmissionError } from "redux-form";
import _ from "underscore";

import { Alert, Typography } from "@braintrust/braintrust-ui-components";
import { openOfferRequiresAcceptModal } from "@js/apps/invoices/components/offer-requires-accept-modal";
import { useGetWithdrawalMethodsQuery } from "@js/apps/withdrawal/api/api";
import {
  ModalConfirm,
  ModalInstance,
  openModalAndWaitForInput,
} from "@js/components/modal";
import { useNavigate, useQueryParams } from "@js/hooks";
import { getFormSubmitError } from "@js/utils";

import {
  useCreateFreelancerInvoiceMutation,
  useGetFreelancerInvoiceQuery,
  useLazyGetEmployerAddressQuery,
  useLazyGetInitialInvoiceDataQuery,
} from "../../api";
import type { InvoiceFormData } from "../../forms/add-invoice/types";
import { useJobSelectList } from "../job-select-list";

import {
  getAddInvoiceInitialValues,
  getInitialValuesForNewInvoice,
  isPONumberRequired,
} from "./helpers";
import { useInvoiceIssueDate } from "./hooks";

export const useAddInvoice = () => {
  const {
    copy: idOfInvoiceToCopyParam,
    job: jobIdParam,
    expense_only: expenseOnlyParam,
  } = useQueryParams();
  const idOfInvoiceToCopy = idOfInvoiceToCopyParam as string;
  const jobId = jobIdParam ? +jobIdParam : undefined;
  const navigate = useNavigate();

  const [createFreelancerInvoice] = useCreateFreelancerInvoiceMutation();
  const { data: jobSelectList, isLoading: isLoadingJobSelectList } =
    useJobSelectList();
  const { data: invoiceToCopy, isLoading: isLoadingInvoice } =
    useGetFreelancerInvoiceQuery(+idOfInvoiceToCopy, {
      skip: !idOfInvoiceToCopy,
    });

  const isExpenseOnlyInvoiceForm =
    Boolean(expenseOnlyParam) || Boolean(invoiceToCopy?.is_expense);

  const [fetchEmployersAddress] = useLazyGetEmployerAddressQuery();

  const loading = isLoadingJobSelectList || isLoadingInvoice;

  const [fetchInitialInvoiceData, { data: initialInvoiceData }] =
    useLazyGetInitialInvoiceDataQuery();
  const issueDate = useInvoiceIssueDate();
  const { data: withdrawalMethods } = useGetWithdrawalMethodsQuery();

  const isStripeDefaultWithdrawalMethod =
    withdrawalMethods?.find((method) => method.is_default)?.content_type ===
    ENUMS.WithdrawalContentType.StripeWithdrawalMethod;

  const onSubmit = async (values: InvoiceFormData) => {
    if (isPONumberRequired(values, initialInvoiceData)) {
      await openModalAndWaitForInput(
        {
          children: (
            <>
              <Typography component="h2" variant="title" size="small">
                PO # Required
              </Typography>
              <Typography component="p" sx={{ maxWidth: "550px" }}>
                This client requires a PO # on each invoice. Please add the
                correct PO #. You may submit the invoice without a PO if one has
                not been assigned, but this could delay your payment. If you
                need help determining the PO for this job, please contact{" "}
                <a
                  className="text-underline"
                  href="mailto:accounting@usebraintrust.com"
                >
                  accounting@usebraintrust.com
                </a>
                .
              </Typography>
            </>
          ),
        },
        { confirmText: "Create Invoice" },
      );
    } else {
      await openModalAndWaitForInput(
        {
          children: (
            <>
              Are you sure all invoice details are accurate?
              {isStripeDefaultWithdrawalMethod && (
                <Alert type="info" className="mt+">
                  <Typography
                    component="p"
                    size="small"
                    color="blue"
                    fontWeight={500}
                  >
                    Stripe charges a {SETTINGS.STRIPE_WITHDRAWAL_FEE}% fee for
                    payments. This equates to a fee of USD.
                  </Typography>
                </Alert>
              )}
            </>
          ),
        },
        { confirmText: "Create Invoice" },
      );
    }

    return createFreelancerInvoice({
      ...values,
      is_expense: isExpenseOnlyInvoiceForm,
    })
      .unwrap()
      .catch(async (e) => {
        const error = getFormSubmitError(e);
        const generalError = error?.data?._error
          ? String(error.data._error)
          : undefined;

        const offerRequiringAcceptanceErrorText =
          "Invoice cannot be created, accept new contract terms first.";
        const isOfferRequiringAcceptanceError =
          !!generalError &&
          generalError.startsWith(offerRequiringAcceptanceErrorText);
        const valuesJobId = values.job;
        if (isOfferRequiringAcceptanceError && valuesJobId) {
          await fetchInitialInvoiceData({ job: valuesJobId })
            .unwrap()
            .then((data) => {
              const offerToAccept = data?.offer_requiring_acceptance_id;
              if (offerToAccept) {
                openOfferRequiresAcceptModal({
                  jobId: valuesJobId,
                  offerId: offerToAccept,
                });
              }
            })
            .catch(() => null);
        }

        throw new SubmissionError(error?.data as object);
      });
  };

  const onSubmitSuccess = () => {
    navigate("/talent/invoices/");
  };

  const onSubmitFail = (
    errors: Record<string, string>,
    _dispatch: unknown,
    submitError: Record<string, string>,
  ) => {
    if (submitError?.cancelledByUser) {
      return;
    }

    if (
      errors?.error_codes &&
      errors.error_codes.includes(
        ENUMS.InvoiceNumberError.DUPLICATED_NUMBER_ERROR,
      )
    ) {
      ModalInstance.open({
        children: (
          <ModalConfirm onConfirm={ModalInstance.close}>
            <div style={{ maxWidth: 505 }}>
              <Typography
                component="h2"
                variant="title"
                fontWeight={400}
                size="small"
                className="mt0"
              >
                Duplicate invoice Number
              </Typography>
              <Typography component="p">
                The client has already received this invoice number from another
                Braintrust related invoice. Please create a new invoice number.
              </Typography>
            </div>
          </ModalConfirm>
        ),
      });

      return;
    }
  };

  const initialValues = useMemo(() => {
    const suggestedInvoiceNumber = initialInvoiceData?.new_invoice_number;
    const wiseFee = initialInvoiceData?.wise_fee ?? undefined;
    return getAddInvoiceInitialValues({
      suggestedInvoiceNumber,
      invoiceToCopy,
      idOfInvoiceToCopy,
      jobIdParam: jobId,
      jobSelectList,
      issueDate,
      isExpenseOnlyInvoiceForm,
      wiseFee,
    });
  }, [
    initialInvoiceData,
    invoiceToCopy,
    idOfInvoiceToCopy,
    jobId,
    jobSelectList,
    issueDate,
    isExpenseOnlyInvoiceForm,
  ]);

  const onChange = (
    values: InvoiceFormData,
    _dispatch: unknown,
    props: DecoratedFormProps<InvoiceFormData, unknown, InvoiceFormData>,
    previousValues: InvoiceFormData,
  ) => {
    if (values.job === previousValues.job) {
      return;
    }

    const job = _.findWhere(jobSelectList, { id: values.job });
    if (!job) {
      return;
    }

    void fetchEmployersAddress(job.employer.id);
    fetchInitialInvoiceData({ job: job.id })
      .unwrap()
      .then((data) => {
        const suggestedInvoiceNumber = data?.new_invoice_number;
        const wiseFee = data?.wise_fee ?? undefined;
        const offerToAccept = data?.offer_requiring_acceptance_id;
        const talentInvoiceEditingDisabled =
          data.talent_invoice_editing_disabled ?? false;
        const offerPayRate = data.offer_pay_rate ?? undefined;
        if (offerToAccept) {
          openOfferRequiresAcceptModal({
            jobId: job.id,
            offerId: offerToAccept,
          });
        }

        if (props.pristine) {
          props.initialize?.(
            getAddInvoiceInitialValues({
              idOfInvoiceToCopy,
              invoiceToCopy,
              suggestedInvoiceNumber,
              jobIdParam: jobId,
              jobSelectList,
              issueDate,
              isExpenseOnlyInvoiceForm,
              wiseFee,
              talentInvoiceEditingDisabled,
              offerPayRate,
            }),
            false,
          );
        } else {
          props.initialize?.(
            getInitialValuesForNewInvoice({
              ...previousValues,
              suggestedInvoiceNumber,
              jobIdParam: job.id,
              jobSelectList,
              issueDate,
              wiseFee,
              talentInvoiceEditingDisabled,
              offerPayRate,
            }),
            false,
          );
        }
      })
      .catch(() => null);
  };

  const canChangeJob = !jobIdParam;
  const isCopy = !!idOfInvoiceToCopy;
  const isInvoiceEditingDisabled =
    !initialInvoiceData || !!initialInvoiceData.talent_invoice_editing_disabled;

  return {
    enableReinitialize: false,
    onSubmit,
    onSubmitSuccess,
    onSubmitFail,
    onChange,
    initialValues,
    initialInvoiceData,
    loading,
    canChangeJob,
    isCopy,
    isExpenseOnlyInvoiceForm,
    isInvoiceEditingDisabled,
  };
};
